import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import duration from "dayjs/plugin/duration";
import { useDispatch } from "react-redux";
import { setBuffer } from "Redux/Screen/Action";
import DateTime from "Utils/DateTime";
import { PatientInitialStateType, UserInitialStateType } from "DataTypes/Redux";
import { useSelector } from "react-redux";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);

interface UseMonthEndSchedulerOptions {
  orgTimezone: string;
  // callback when activity forcibly ended
  onForceExit?: (alertText: string) => void;
  showCountdownOnUI: boolean;
  bufferTimeInSec: number
}

interface StartCounterType {
  target: dayjs.Dayjs,
}

const useMonthEndSchedulerWithSmartCountdown = (
  props: UseMonthEndSchedulerOptions
): {
  isInBuffer: boolean,
  countdown: string
} => {
  const {
    orgTimezone,
    onForceExit,
    showCountdownOnUI,
    bufferTimeInSec
  } = props;

  const dispatch = useDispatch();
  const { patientOrg }: PatientInitialStateType = useSelector(
    (state: any) => state?.patient
  );
  const { timezoneMonthEndTimeArr }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const [isInBuffer, setIsInBuffer] = useState(false);
  const [countdown, setCountdown] = useState<string>("");
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    dispatch(setBuffer(countdown));
  }, [countdown])

  useEffect(() => {
    // 🟢 Start logic immediately on mount

    // run the main scheduler immediately on mount / dependency change
    CalculateSchedule();

    // cleanup when component unmounts or dependencies change
    return () => {

      // clear scheduled timeout
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      // clear per-second interval
      if (intervalRef.current) clearInterval(intervalRef.current);
      dispatch(setBuffer(""));
    };
  }, []);

  // main function that calculates windows and schedules next check
  const CalculateSchedule = (): void => {
    // current time in organization's timezone
    const now = DateTime.CurrentDateTimeInPatOrgLocTZ(
      patientOrg.practiceLocationTimezone
    );

    // --- Calculate key boundaries in org timezone ---

    // midnight at start of next month (orgTZ)
    const startOfNextMonth = now.add(1, "month").startOf("month");
    // const startOfNextMonth = DateTime.ConvertDateTimeInPatOrgLocTZ(
    //   patientOrg.practiceLocationTimezone, "2025-11-21T07:45:00"
    // );
    // const startOfNextMonth = DateTime.FormatToDayJS(
    //   timezoneMonthEndTimeArr.find(
    //     el => el.timezone == patientOrg.practiceLocationTimezone
    //   )?.datetime, patientOrg.practiceLocationTimezone
    // ) || now.add(1, "month").startOf("month");

    // last second of current month in orgTZ
    const monthEnd = startOfNextMonth.subtract(1, "second");

    // buffer start (bufferMinutes before monthEnd)
    const bufferStart = monthEnd;

    // buffer end (bufferMinutes after start of next month)
    const bufferEnd = startOfNextMonth.add(bufferTimeInSec, "second");

    // countdown visible 2 hours before bufferStart
    const countdownStart = bufferStart.subtract(2, "hour");

    // countdown visible till bufferEnd
    const countdownEnd = bufferStart;

    const inCountdown = now.isAfter(countdownStart) &&
      now.isBefore(countdownEnd);
    // --- Determine current states ---
    // true if now is strictly between buffer start and end
    const inBuffer = now.isAfter(bufferStart) && now.isBefore(bufferEnd);
    setIsInBuffer(inBuffer);

    // 🚫 Force exit if user is inside buffer and still doing activity
    if (inBuffer && onForceExit) {
      const diffMs = bufferEnd.diff(now); // milliseconds difference to target
      onForceExit(
        "The billable time for the month is getting processed," +
        " you can resume the services in next " + `
        ${DateTime.CalculateTimeDiff(diffMs)}`
      );
    }

    if (inCountdown && showCountdownOnUI) {
      StartCountdown({ target: countdownEnd });
    } else if (inBuffer && showCountdownOnUI) {
      StartCountdown({ target: bufferEnd });
    } else {
      setCountdown("");
      //   // clear any existing per-second interval
      if (intervalRef.current) clearInterval(intervalRef.current);
    }

    // Schedule next boundary check (minimize background timers)
    let nextCheck: dayjs.Dayjs;

    // next important moment: countdownStart
    if (now.isBefore(countdownStart)) nextCheck = countdownStart;

    // then bufferStart
    else if (now.isBefore(bufferStart)) nextCheck = bufferStart;

    // then bufferEnd
    else if (now.isBefore(bufferEnd)) nextCheck = bufferEnd;

    // otherwise schedule for next month's countdownStart (not really required)
    else nextCheck = startOfNextMonth.add(1, "month").subtract(2, "hour");

    // compute ms until nextCheck; min 10s for safety
    const delay = Math.max(nextCheck.diff(now, "millisecond"), 1000 * 10);

    // schedule a one-shot timeout to re-run calculateSchedule
    timeoutRef.current = setTimeout(CalculateSchedule, delay);
  };

  const StartCountdown = (props: StartCounterType): void => {
    const { target } = props;

    // clear previous interval if any
    if (intervalRef.current) clearInterval(intervalRef.current);

    // start a per-second interval to update countdown text
    intervalRef.current = setInterval(() => {
      const current = dayjs().tz(orgTimezone); // current time in orgTZ
      const diffMs = target.diff(current);
      if (diffMs <= 0) { // if target already reached or passed
        setCountdown("");
        // stop updating once target is reached
        intervalRef.current && clearInterval(intervalRef.current);
        return;
      }
      // set human readable countdown string in state
      setCountdown(DateTime.CalculateTimeDiff(diffMs));
      // run every second
    }, 1000);
  };

  return { isInBuffer, countdown };
}
export default useMonthEndSchedulerWithSmartCountdown;
